/* Engraving input charset filter.
 *
 * Customers were typing IME-composed kaomoji / CJK text (e.g. "o(≧▽≦)o",
 * "(｡･_･｡)ﾉｼ") into engraving fields. Those characters render in the on-page
 * preview font but cannot be engraved, so the orders arrive un-producible.
 * The theme's own per-product validators only count characters — they never
 * restricted WHICH characters — and keydown-based checks miss IME
 * composition and paste entirely.
 *
 * This filter strips every character an engraving field can't produce, on
 * every path a character can get in (typing, paste, drop, IME composition):
 *   kept   — printable ASCII, printable Latin-1 accents (José, Zoë), and the
 *            Icons-font symbol glyphs U+E900–U+E9FF inserted by the
 *            "Add Symbol" picker (see CATEGORY_LIST in assets/1r-theme.js).
 *   mapped — smart quotes/dashes/ellipsis (iOS smart punctuation) and
 *            fullwidth ASCII forms (CJK IMEs) to their ASCII equivalents,
 *            so an iPhone typing "Mom's" doesn't lose its apostrophe.
 *   dropped — everything else (CJK, halfwidth katakana, emoji, math
 *            symbols, control characters, soft hyphens).
 *
 * Stricter per-field rules in 1r-theme.js (e.g. letters-only jersey text)
 * still apply on top; this is the outer fence. Listeners are capture-phase
 * on document, so the theme's own counters/previews always read the
 * already-cleaned value.
 *
 * Fields can opt into a stricter charset with data-wf-charset:
 *   letters-heart — tag line1-letters-heart (letter-charm name pieces):
 *            A-Z only, uppercased, plus any number of hearts (each one is
 *            a charm and counts toward maxlength). The heart is U+2665,
 *            which the backend maps to charm CH-MSN-HRT-G; phone heart
 *            emoji and the outline heart are converted to it.
 */
(function () {
  'use strict';

  var SELECTOR = [
    '.symbol-input',
    '#input-text',
    '#NON_PHOTO_CUSTOM_TEXT_1',
    '#MODAL_TEXT1',
    '#flower-builder-text',
    '#flower-builder-modal-text',
    '#zodiac-name-input',
    '#zodiac-date-input',
    '.custom-bracelet-field input[type="text"]',
    '.custom-ornament-field input[type="text"]',
    '.custom-ring-field input[type="text"]'
  ].join(',');

  /* Typographic characters that mobile keyboards and IMEs substitute for
     plain ASCII — convert rather than drop. Keys are quotes (U+2018/2019/
     201A/201B/2032, U+201C/201D/201E/201F/2033), hyphens/dashes
     (U+2010-2015, U+2212), ellipsis (U+2026), NBSP + ideographic space. */
  var MAP = {
    '‘': "'", '’': "'", '‚': "'", '‛': "'", '′': "'",
    '“': '"', '”': '"', '„': '"', '‟': '"', '″': '"',
    '‐': '-', '‑': '-', '‒': '-', '–': '-', '—': '-',
    '―': '-', '−': '-',
    '…': '...',
    '\u00A0': ' ', '\u3000': ' '
  };

  /* Printable ASCII; printable Latin-1 minus the invisible soft hyphen
     (U+00AD); the Icons-font private-use block used by Add Symbol. */
  var ALLOWED = /[\x20-\x7E\u00A1-\u00AC\u00AE-\u00FF\uE900-\uE9FF]/;

  function sanitize(str) {
    var out = '';
    for (var i = 0; i < str.length; i++) {
      var ch = str.charAt(i);
      if (MAP.hasOwnProperty(ch)) {
        out += MAP[ch];
        continue;
      }
      var code = str.charCodeAt(i);
      if (code >= 0xFF01 && code <= 0xFF5E) {
        // Fullwidth ASCII variants from CJK IMEs -> ASCII
        out += String.fromCharCode(code - 0xFEE0);
        continue;
      }
      if (ALLOWED.test(ch)) out += ch;
    }
    return out;
  }

  var HEART = '\u2665';
  /* ❤ / ❤️ (U+2764), ♥️, ♡ (U+2661), and the emoji hearts
     U+1F493, U+1F495-1F49F, U+1F5A4, U+1F90D-1F90E, U+1F9E1, U+1FA75-1FA77
     (U+1F494, the broken heart, is deliberately left out). */
  var HEART_LIKE = /\u2764\uFE0F?|\u2665\uFE0F?|\u2661|\uD83D[\uDC93\uDC95-\uDC9F\uDDA4]|\uD83E[\uDD0D\uDD0E\uDDE1\uDE75-\uDE77]/g;

  function sanitizeLettersHeart(str) {
    str = str.replace(HEART_LIKE, HEART);
    var out = '';
    for (var i = 0; i < str.length; i++) {
      var ch = str.charAt(i);
      if (ch === HEART) {
        out += ch;
        continue;
      }
      var code = str.charCodeAt(i);
      if (code >= 0xFF21 && code <= 0xFF5A) {
        // Fullwidth letters from CJK IMEs -> ASCII
        ch = String.fromCharCode(code - 0xFEE0);
      }
      if (/[A-Za-z]/.test(ch)) out += ch.toUpperCase();
    }
    return out;
  }

  var CHARSETS = { 'letters-heart': sanitizeLettersHeart };

  /* 1r-theme.js checks this to leave data-wf-charset fields alone. */
  window.wfCharsetFilter = CHARSETS;

  /* Returns true when the value was modified. */
  function applyFilter(input) {
    var raw = input.value;
    var cleanFn = CHARSETS[input.getAttribute('data-wf-charset')] || sanitize;
    var clean = cleanFn(raw);
    if (input.maxLength > 0 && clean.length > input.maxLength) {
      clean = clean.slice(0, input.maxLength);
    }
    if (clean === raw) return false;

    var caret = null;
    try { caret = input.selectionStart; } catch (e) { /* non-text state */ }
    input.value = clean;
    if (caret !== null && caret !== undefined) {
      var pos = Math.min(cleanFn(raw.slice(0, caret)).length, clean.length);
      try { input.setSelectionRange(pos, pos); } catch (e) { /* ignore */ }
    }
    return true;
  }

  function matches(el) {
    return el && el.matches && el.matches(SELECTOR);
  }

  var composing = false;

  document.addEventListener('compositionstart', function (e) {
    if (matches(e.target)) composing = true;
  }, true);

  /* While an IME composition is in flight, rewriting .value breaks the
     composition buffer — wait for compositionend, then clean up and re-fire
     input/change so the theme's counters and live previews resync. */
  document.addEventListener('compositionend', function (e) {
    if (!matches(e.target)) return;
    composing = false;
    if (applyFilter(e.target)) {
      e.target.dispatchEvent(new Event('input', { bubbles: true }));
      e.target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }, true);

  document.addEventListener('input', function (e) {
    if (!matches(e.target)) return;
    if (e.isComposing || composing) return;
    applyFilter(e.target);
  }, true);

  /* Backstop for value changes that skip input events (autofill, scripts). */
  document.addEventListener('change', function (e) {
    if (matches(e.target)) applyFilter(e.target);
  }, true);
})();
